import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Star, 
  MessageSquare, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Send,
  Mail,
  RefreshCw,
  Sparkles,
  ExternalLink,
  User,
  ChevronDown
} from "lucide-react";
import { adminFetch } from "@/lib/adminAuth";
import Navbar from "@/components/Navbar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star rating component
const StarRating = ({ rating, size = "sm" }) => {
  const sizes = { sm: "w-4 h-4", md: "w-5 h-5", lg: "w-6 h-6" };
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizes[size]} ${
            star <= rating ? "fill-yellow-400 text-yellow-400" : "text-slate-300"
          }`}
        />
      ))}
    </div>
  );
};

// Rating distribution bar
const RatingBar = ({ stars, count, total }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-3 text-slate-600">{stars}</span>
      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-yellow-400 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="w-8 text-right text-slate-500">{count}</span>
    </div>
  );
};

// Individual review card
const ReviewCard = ({ review, onGenerateResponse }) => {
  const [showResponse, setShowResponse] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [generating, setGenerating] = useState(false);

  const handleGenerateResponse = async () => {
    setGenerating(true);
    const response = await onGenerateResponse(review);
    setAiResponse(response);
    setShowResponse(true);
    setGenerating(false);
  };

  return (
    <Card className="border-slate-200" data-testid={`review-${review.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
            {review.author_avatar ? (
              <img src={review.author_avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <User className="w-5 h-5 text-slate-400" />
            )}
          </div>

          <div className="flex-1">
            {/* Header */}
            <div className="flex items-center justify-between mb-1">
              <div>
                <span className="font-medium text-slate-900">{review.author_name}</span>
                <Badge 
                  variant="outline" 
                  className={`ml-2 text-xs ${
                    review.platform === "google" ? "border-blue-200 text-blue-600" : "border-blue-800 text-blue-800"
                  }`}
                >
                  {review.platform === "google" ? "Google" : "Facebook"}
                </Badge>
              </div>
              <span className="text-sm text-slate-500">{review.relative_time || review.review_date}</span>
            </div>

            {/* Rating */}
            <div className="mb-2">
              <StarRating rating={review.rating} />
            </div>

            {/* Review text */}
            {review.text && (
              <p className="text-slate-700 text-sm mb-3">{review.text}</p>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateResponse}
                disabled={generating}
                className="text-xs"
                data-testid={`generate-response-${review.id}`}
              >
                {generating ? (
                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3 mr-1" />
                )}
                Generate Response
              </Button>
              {review.source_url && (
                <a href={review.source_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="sm" className="text-xs">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    View Original
                  </Button>
                </a>
              )}
            </div>

            {/* AI Response */}
            {showResponse && aiResponse && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">AI Suggested Response</span>
                </div>
                <p className="text-sm text-slate-700">{aiResponse}</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700">
                    Copy to Clipboard
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Email invite modal
const EmailInviteForm = ({ businessId, businessName, onClose }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!email || !name) return;
    
    setSending(true);
    try {
      const response = await adminFetch(`${API_URL}/api/email/review-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          business_id: businessId,
          customer_email: email,
          customer_name: name,
          use_ai_content: true
        })
      });
      
      if (response.ok) {
        setSent(true);
        setTimeout(() => {
          setEmail("");
          setName("");
          setSent(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Failed to send invite:", error);
    }
    setSending(false);
  };

  return (
    <Card className="border-blue-200 bg-blue-50/50" data-testid="email-invite-form">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          Send Review Invite
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          placeholder="Customer name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="invite-name-input"
        />
        <Input
          type="email"
          placeholder="Customer email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="invite-email-input"
        />
        <Button 
          onClick={handleSend} 
          disabled={sending || !email || !name}
          className="w-full bg-blue-600 hover:bg-blue-700"
          data-testid="send-invite-btn"
        >
          {sending ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : sent ? (
            "✓ Sent!"
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send AI-Powered Invite
            </>
          )}
        </Button>
        <p className="text-xs text-slate-500 text-center">
          Uses AI to generate personalized invite content
        </p>
      </CardContent>
    </Card>
  );
};

export const ReviewsDashboard = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState("all");

  useEffect(() => {
    fetchReviews();
  }, [businessId]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      // Fetch business details
      const bizResponse = await adminFetch(`${API_URL}/api/businesses`);
      if (bizResponse.ok) {
        const businesses = await bizResponse.json();
        const biz = businesses.find(b => b.id === businessId);
        setBusiness(biz);
      }

      // Fetch reviews
      const response = await adminFetch(`${API_URL}/api/reviews/aggregate/${businessId}`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
        setSummary(data.summary);
      } else if (response.status === 404) {
        setError("Business not found");
      }
    } catch (err) {
      console.error("Failed to fetch reviews:", err);
      setError("Failed to load reviews");
    }
    setLoading(false);
  };

  const handleGenerateResponse = async (review) => {
    try {
      const response = await adminFetch(`${API_URL}/api/reviews/generate-response`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          review_id: review.id,
          rating: review.rating,
          text: review.text,
          author_name: review.author_name,
          platform: review.platform,
          business_name: business?.name || "Business",
          tone: "friendly"
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.suggested_response;
      }
    } catch (err) {
      console.error("Failed to generate response:", err);
    }
    return "Unable to generate response. Please try again.";
  };

  const filteredReviews = filterPlatform === "all" 
    ? reviews 
    : reviews.filter(r => r.platform === filterPlatform);

  const getTrendIcon = () => {
    if (!summary) return <Minus className="w-4 h-4 text-slate-400" />;
    if (summary.recent_trend === "improving") return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (summary.recent_trend === "declining") return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20 flex items-center justify-center">
        <Navbar />
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Error</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate("/lookup")}>Find a Business</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-12" data-testid="reviews-dashboard">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {business?.name || "Business"} Reviews
          </h1>
          <p className="text-slate-600">
            Aggregated reviews from Google & Facebook
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary Card */}
            <Card className="border-slate-200" data-testid="reviews-summary">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {/* Average Rating */}
                  <div className="text-center">
                    <div className="text-4xl font-bold text-slate-900 mb-1">
                      {summary?.average_rating?.toFixed(1) || "0.0"}
                    </div>
                    <StarRating rating={summary?.average_rating || 0} size="md" />
                    <p className="text-sm text-slate-500 mt-1">Average Rating</p>
                  </div>

                  {/* Total Reviews */}
                  <div className="text-center">
                    <div className="text-4xl font-bold text-slate-900 mb-1">
                      {summary?.total_reviews || 0}
                    </div>
                    <MessageSquare className="w-5 h-5 mx-auto text-blue-600" />
                    <p className="text-sm text-slate-500 mt-1">Total Reviews</p>
                  </div>

                  {/* Response Rate */}
                  <div className="text-center">
                    <div className="text-4xl font-bold text-slate-900 mb-1">
                      {summary?.response_rate || 0}%
                    </div>
                    <Send className="w-5 h-5 mx-auto text-green-600" />
                    <p className="text-sm text-slate-500 mt-1">Response Rate</p>
                  </div>

                  {/* Trend */}
                  <div className="text-center">
                    <div className="text-4xl font-bold text-slate-900 mb-1 flex items-center justify-center gap-2">
                      {getTrendIcon()}
                      <span className="capitalize">{summary?.recent_trend || "Stable"}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">Recent Trend</p>
                  </div>
                </div>

                {/* Rating Distribution */}
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <h3 className="text-sm font-medium text-slate-700 mb-3">Rating Distribution</h3>
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((stars) => (
                      <RatingBar
                        key={stars}
                        stars={stars}
                        count={summary?.rating_distribution?.[stars] || 0}
                        total={summary?.total_reviews || 0}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filter */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">Filter by:</span>
              <div className="flex gap-2">
                {["all", "google", "facebook"].map((platform) => (
                  <Button
                    key={platform}
                    variant={filterPlatform === platform ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterPlatform(platform)}
                    className={filterPlatform === platform ? "bg-blue-600" : ""}
                    data-testid={`filter-${platform}`}
                  >
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchReviews}
                className="ml-auto"
                data-testid="refresh-reviews"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>

            {/* Reviews List */}
            <div className="space-y-4">
              {filteredReviews.length > 0 ? (
                filteredReviews.map((review) => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    onGenerateResponse={handleGenerateResponse}
                  />
                ))
              ) : (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-700 mb-2">No Reviews Yet</h3>
                    <p className="text-slate-500 mb-4">
                      Connect your Google Business Profile or Facebook Page to start aggregating reviews.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Email Invite */}
            <EmailInviteForm 
              businessId={businessId} 
              businessName={business?.name}
            />

            {/* Quick Actions */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/report/${businessId}`)}
                  data-testid="view-report-btn"
                >
                  <ChevronDown className="w-4 h-4 mr-2 rotate-[-90deg]" />
                  View Snapshot Report
                </Button>
                {business?.place_id && (
                  <a 
                    href={`https://search.google.com/local/writereview?placeid=${business.place_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="w-full justify-start">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Get Google Review Link
                    </Button>
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Platform Status */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Connected Platforms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-bold text-sm">G</span>
                    </div>
                    <span className="text-sm font-medium">Google</span>
                  </div>
                  <Badge variant={business?.place_id ? "default" : "secondary"}>
                    {business?.place_id ? "Connected" : "Not Connected"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-blue-800 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">f</span>
                    </div>
                    <span className="text-sm font-medium">Facebook</span>
                  </div>
                  <Badge variant="secondary">Not Connected</Badge>
                </div>
                <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                  Connect more platforms to aggregate all your reviews in one place.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsDashboard;
